import {
    counterRelationships as cr,
    FormStructure as fs
} from "../consts/formStructure";

export function coalesce(given, backup) {
    return given !== undefined ? given : backup
}

export function sortObj(obj, isReverse=false) {
    if (isReverse) {
        return Object.keys(obj || {}).sort().reverse().reduce(function (result, key) {
            result[key] = obj[key];
            return result;
        }, {});
    } else {
        return Object.keys(obj || {}).sort().reduce(function (result, key) {
        result[key] = obj[key];
        return result;
        }, {});    }
}

// initFormData: To set initial (tab) data and section statuses using the default values given in formStructure
// params:
// - setInputData (hook function)
export function initFormData( setInputData ) {
    const defaultData = Object.fromEntries(
        Object.entries(fs).map(([category, tab]) => {
            let tabData = {};
            tab.sections.forEach((section) => {
                section.subsections.forEach((subsection) => {
                    subsection.fields.forEach((field) => {
                        tabData[field.id_name] = field?.default_val ?? null;
                    });
                });
            });
            return [category, tabData];
        })
    );
    setInputData(defaultData);
}

// initCounterData
// - inputData (hook data object)
// - setCounterData (hook function)
export function initCounterData( inputData, setCounterData ) {
    let defaultData = {};

    Object.entries(cr).forEach(([key, data]) => {
        defaultData[key] = {
            // Attributes updated on user input
            value_a: null, // sum to update
            value_b: null, // sum to match
            isMismatched: false, // true if value_a != value_b

            // Attributes default set in const formStructure
            dependentOn: data.dependentOn, // determines whether counter is overridden if this dependency condition is met
            isAlwaysShown: data.isAlwaysShown, // determines whether counter is shown, but modal sect is always shown
            tab_a: data.group1.tab, // fs given value
            label_a: data.group1.label, // [] need to join
            tab_b: data.group2?.tab, // fs given value
            label_b: data.group2?.label, // [] need to join
            tag: data.tag, // to use to navigate to specific tab
        };
    });
    updateCounterData( inputData, defaultData, setCounterData )
}

// updateOverallData: Event handler used to update overall data every time tab data is changed
// params:
// - fieldData (data object derived from event (target and value))
// - setInputData (hook function)
// - category (global/onsite/transportation/hotel)
export function updateOverallData(fieldData, setInputData, category) {
    const { name: targetName, value } = fieldData;
    if (!targetName) return;

    const targetVal = value !== "" ? value : null;

    setInputData(prevData => ({
        ...prevData,
        [category]: {
            ...prevData[category],
            [targetName]: targetVal
        }
    }));
}

function parseInputValue(input) {
    if (input !== undefined) {
        if (input === null) return 0;
        else if (typeof input === "string") {
            // Handle range options with support for negative numbers, e.g., "-10--5" gets evaluated as -7.5
            if (input.indexOf("-") !== -1 && input.indexOf("e") === -1) {
                // This regular expression will split by a hyphen not preceded by a number or followed by a number (avoiding splitting negative signs)
                var values = input.split(/(?<!\d)-|-(?!\d)/);
                return values.reduce((a, b) => parseFloat(a) + parseFloat(b)) / values.length;
            } else {
                try { return parseFloat(input); }
                catch (e) {}
                try { return parseInt(input); }
                catch (e) {}
            }
        }
        return input;
    } else return 0;
}
// updateCounterData
// - inputData (hook data object)
// - counterData (hook data object)
// - setCounterData (hook function)
// - selectedFootprintCategories (dict of categories selected)
export function updateCounterData( inputData, counterData, setCounterData, selectedFootprintCategories={}  ) {
    const newCounterData = {};
    Object.entries(counterData).forEach(([key, data]) => {
        const newData = { ...data };
        const category = cr[key].category;
        const inputs_a_list = cr[key].group1.fields;
        const inputs_b_list = cr[key].group2?.fields;

        // Sum group A inputs
        newData['value_a'] = inputs_a_list.reduce((sum, input) =>
            sum + parseInputValue(inputData?.[category]?.[input]), 0);

        // Sum group B inputs
        newData['value_b'] = inputs_b_list
        ? Math.ceil(inputs_b_list.reduce((sum, input) =>
            sum + parseInputValue(inputData?.['global']?.[input]), 0))
        : 100;

        // Conditions
        let input_mismatch_global = (newData['value_a'] !== newData['value_b'] && data.isAlwaysShown);
        let input_exceeds_global = (newData['value_a'] > newData['value_b'] && !data.isAlwaysShown);
        let input_ignored = false;

        if (data.dependentOn) {
            const [depField, depValue] = data.dependentOn.split(":");
            input_ignored = inputData?.[category]?.[depField] !== depValue;
        }
        if (category !== 'global') {
            // For case where footprint category is not included
            input_ignored = (input_ignored || !selectedFootprintCategories?.[category]); 
        }

        newData['isMismatched'] = !input_ignored && (input_mismatch_global || input_exceeds_global);

        newCounterData[key] = newData;
    });
    setCounterData(newCounterData);
}

// Parameters:
// httpmethod (GET / POST)
// payload {referenceId:str / body:Object}
export function callMCCAPI( httpmethod='POST', payload=null ) {
    const url = process.env.REACT_APP_CC_LAMBDA + (httpmethod==='GET' ? `?${new URLSearchParams({ referenceId: String(payload) })}` : '');
    return fetch(url, 
    {
        method: httpmethod, // GET or POST
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(httpmethod === 'POST' && { body: JSON.stringify(payload || {}) })
    }).then((response) => {
        return response.json();
    }).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
            if (typeof error.json === "function") {
                error.json().then(jsonError => {
                    console.log("Json error from API");
                    console.log(jsonError);
                }).catch(genericError => {
                    console.log("Generic error from API");
                    console.log(error.statusText);
                });
            } else {
                console.log("Fetch error");
                console.log(error);
            }
        }
    })
}
